import { PillVariant } from 'fe-design-base/molecules/Pill/Pill';

import { toI18n } from 'util/i18n';

import { RequestType } from './types';

type Status =
  | 'approved'
  | 'pending_approval'
  | 'awaiting_responses'
  | 'needs_reply'
  | 'no_answer'
  | 'denied_by_manager'
  | 'declined';

type UI = { label: string; variant: PillVariant };

const BASE_STATUS_UI: Record<Status, UI> = {
  approved: {
    label: toI18n('requests_feed.statuses.approved'),
    variant: 'success',
  },
  pending_approval: {
    label: toI18n('requests_feed.statuses.pending_approval'),
    variant: 'warning',
  },
  awaiting_responses: {
    label: toI18n('requests_feed.statuses.awaiting_responses'),
    variant: 'warning',
  },
  needs_reply: {
    label: toI18n('requests_feed.statuses.needs_reply'),
    variant: 'warning',
  },
  no_answer: {
    label: toI18n('requests_feed.statuses.no_answer'),
    variant: 'warning',
  },
  denied_by_manager: {
    label: toI18n('requests_feed.statuses.denied_by_manager'),
    variant: 'error',
  },
  declined: {
    label: toI18n('requests_feed.statuses.declined'),
    variant: 'error',
  },
};

const REQUEST_ALLOWED: Record<RequestType, readonly Status[]> = {
  trade: [
    'approved',
    'pending_approval',
    'awaiting_responses',
    'needs_reply',
    'no_answer',
    'declined',
    'denied_by_manager',
  ],
  cover: [
    'approved',
    'pending_approval',
    'awaiting_responses',
    'needs_reply',
    'denied_by_manager',
    'declined',
  ],
  claim: [
    'approved',
    'pending_approval',
    'awaiting_responses',
    'needs_reply',
    'denied_by_manager',
  ],
  time_off: [
    'approved',
    'awaiting_responses',
    'needs_reply',
    'denied_by_manager',
  ],
  availability: [
    'approved',
    'awaiting_responses',
    'needs_reply',
    'denied_by_manager',
    'pending_approval',
  ],
};

const REQUEST_OVERRIDES: Partial<
  Record<RequestType, Partial<Record<Status, UI>>>
> = {
  cover: {
    awaiting_responses: {
      label: toI18n('requests_feed.statuses.seeking_cover'),
      variant: 'info',
    },
  },
  time_off: {
    awaiting_responses: {
      label: toI18n('requests_feed.statuses.pending_approval'),
      variant: 'warning',
    },
  },
  trade: {
    awaiting_responses: {
      label: toI18n('requests_feed.statuses.seeking_trade'),
      variant: 'info',
    },
  },
  claim: {
    awaiting_responses: {
      label: toI18n('requests_feed.statuses.pending_approval'),
      variant: 'warning',
    },
    needs_reply: {
      label: toI18n('requests_feed.statuses.pending_approval'),
      variant: 'warning',
    },
  },
};

export const STATUS_UI = Object.fromEntries(
  Object.entries(REQUEST_ALLOWED).map(([type, statuses]) => {
    const overrides = REQUEST_OVERRIDES[type as RequestType] ?? {};
    const entries = Object.fromEntries(
      statuses.map(status => [
        status,
        overrides[status] ?? BASE_STATUS_UI[status],
      ])
    );
    return [type, entries];
  })
) as Record<RequestType, Record<Status, UI>>;
